'use client';
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CardProps,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useMemo } from 'react';

import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { ResourceCategory, UploadFile } from '@/types';

interface ResourceResultProps extends CardProps {
  title: string;
  description?: string | undefined;
  thumbnail?: UploadFile | undefined;
  slug: string;
  resourceCategory?: ResourceCategory;
  showCategory?: boolean;
}

const MAX_DESCRIPTION_LENGTH = 85;

export function ResourceResult({
  title,
  description = undefined,
  thumbnail = undefined,
  slug,
  resourceCategory,
  showCategory = false,
  ...props
}: ResourceResultProps) {
  const theme = useTheme();

  // If the resourceCategory is not provided, we assume the slug is a full path
  const link = useMemo(
    () => (resourceCategory ? `/resources/${resourceCategory.Slug}/${slug}` : slug),
    [slug, resourceCategory]
  );
  return (
    <>
      <Card
        data-component="ResourceResult"
        variant="outlined-fine"
        {...props}
        sx={{
          width: '20rem',
          border: 0,
          background: 'none',
          p: 0,

          '& .MuiCardActionArea-root': {
            border: 0,
            borderRadius: 4,
            backgroundColor: theme.palette.purple[700],
            color: theme.palette.common.white,
          },

          '& .MuiCardMedia-root': {
            backgroundColor: theme.palette.common.white,
            borderRadius: 3,
            overflow: 'hidden',
            height: '15rem',
          },

          '& .MuiCardContent-root': {
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            top: -1,
            bottom: -1,
            left: -1,
            right: -1,
            padding: 2.5,
            background: theme.palette.gradients.imageOverlayThumbnail,
            borderRadius: 3,
          },
        }}
      >
        <CardActionArea href={link}>
          {getImageUrl({ image: thumbnail, size: 'url' }) && (
            <CardMedia
              component={'img'}
              sx={{ height: '12rem' }}
              image={getImageUrl({ image: thumbnail, size: 'url' })!}
              alt={`${thumbnail?.alternativeText ?? ''}`}
            />
          )}
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {showCategory && resourceCategory?.CategoryName && (
              <Chip
                label={resourceCategory.CategoryName}
                size="small"
                sx={{
                  color: theme.palette.common.black,
                  backgroundColor: theme.palette.purple[300],
                }}
              />
            )}
            <Typography
              color="common.white"
              variant="large"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textShadow: `0 0 0.5rem ${theme.palette.purple[900]}`,
              }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                variant="small"
                sx={{
                  color: theme.palette.purple[300],
                  textShadow: `0 0 0.5rem ${theme.palette.purple[800]}`,
                }}
              >
                {description.length > MAX_DESCRIPTION_LENGTH
                  ? `${description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
                  : description}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </>
  );
}
